import { HotFilterItem, HotFiltersFacetsFields } from '@interfaces/api/responses/hot-filters.response';

export const ALLOWED_HOT_FILTER_FIELDS: HotFiltersFacetsFields[] = [
  'brand',
  'categoryLvl0',
  'categoryLvl1',
  'categoryLvl2',
  'materialLvl0',
  'materialLvl1',
  'color',
  'condition',
  'universe',
];

// Maps filter categories to their hierarchical levels
export const HOT_FILTER_KEY_MAPPINGS = {
  category: ['categoryLvl0', 'categoryLvl1', 'categoryLvl2'],
  material: ['materialLvl0', 'materialLvl1'],
};

export const isAllowedHotFilterInUrlParams = (input: Record<string, string>): boolean => {
  const inputKeys = Object.keys(input);

  const directMatch = inputKeys.some((key) => ALLOWED_HOT_FILTER_FIELDS.includes(key as HotFiltersFacetsFields));

  const nestedMatch = Object.keys(HOT_FILTER_KEY_MAPPINGS).some(
    (mappingKey) =>
      inputKeys.includes(mappingKey) &&
      HOT_FILTER_KEY_MAPPINGS[mappingKey].some((allowedField) => ALLOWED_HOT_FILTER_FIELDS.includes(allowedField)),
  );

  return directMatch || nestedMatch;
};

// TODO: Change with FAVORITE_PAGE Keys
export const HOT_FILTER_KEYS = {
  brand: 'FAVORITE_PAGE.FILTER.BRAND',
  category: 'FAVORITE_PAGE.FILTER.CATEGORY',
  color: 'FAVORITE_PAGE.FILTER.COLOR',
  condition: 'FAVORITE_PAGE.FILTER.CONDITION',
  material: 'FAVORITE_PAGE.FILTER.MATERIAL',
  all_items: 'FAVORITE_PAGE.FILTER.SUB_CATEGORIES_ALL_ITEMS',
};

export const getTranslatedHotFilterName = (name: string): string => {
  return HOT_FILTER_KEYS[name.toLowerCase() as keyof typeof HOT_FILTER_KEYS] || name;
};

/**
 * Parses a hierarchical string into two levels of IDs based on segments separated by '__'.
 * Each segment contains items separated by '_', with IDs prefixed by '#'.
 * - Input: "Clothing#2^1>Coats#20^2_Dresses#15^2_Skirts#17^2_Swimwear#502^2__Accessories#10^3>Bags & Pencil cases#92^10_Hats & Gloves#88^10"
 * - Output: { "lvl0": [2, 10], "lvl1": [20, 15, 17, 502, 92, 88], "universe": [1, 3] }
 */

const parseCategoryUrlFragment = (items: string[]) => {
  const result = {
    Lvl0: [],
    Lvl1: [],
    universe: [],
  };

  items.forEach((item) => {
    // Split the string by '>' to handle hierarchical levels
    const parts = item.split('>');

    // Process the first part (index 0) for universe
    const firstPart = parts[0];
    // Find all occurrences of ^ followed by digits
    const universeMatches = firstPart.match(/\^(\d+)/g);

    if (universeMatches) {
      universeMatches.forEach((match) => {
        // Remove the '^' and parse the number
        const universeID = match.substring(1);
        if (!result.universe.includes(universeID)) {
          result.universe.push(universeID);
        }
      });
    }

    // Now process all the parts (including the first part) to find lvl0 and lvl1
    parts.forEach((part, index) => {
      const subParts = part.split('_'); // Split by '_' for individual items

      subParts.forEach((subPart) => {
        const [, idStr, parentIDStr] = subPart.split('#');

        // Extract ID before '^' (e.g., "2^1" → "2")
        const id = idStr?.split('^')[0];
        const parentID = parentIDStr ? parentIDStr : null;

        // Handle lvl0 and lvl1 (categorizing items by depth)
        if (index === 0 && !parentID) {
          result.Lvl0.push(id); // Add to lvl0 if no parent
        } else if (!parentID) {
          result.Lvl1.push(id); // Otherwise, it's a lvl1 item
        }

        // Add parentID to universe if it exists
        if (parentID && !result.universe.includes(parentID)) {
          result.universe.push(parentID);
        }
      });
    });
  });

  result.Lvl0 = [...new Set(result.Lvl0)];
  result.Lvl1 = [...new Set(result.Lvl1)];
  result.universe = [...new Set(result.universe)];

  return result;
};

/**
 * Parses a hierarchical string into two levels of IDs based on segments separated by '>'.
 * Each segment contains items separated by '_', with IDs prefixed by '#'.
 * - Input: "material=Exotic+leathers%2352>Alligator%2371%5E52_Crocodile%2370%5E52_Denim+-+Jeans%235"
 * - Output: { "Lvl0": ["52", "5"], "Lvl1": ["71", "70"] }
 */
export const parseHierarchicalString = (input: string): Record<string, string[]> => {
  const output: Record<string, string[]> = {
    Lvl0: [],
    Lvl1: [],
  };

  // Split the input string into hierarchical segments based on '>'
  const segments = input.split('>');

  // Process the first segment
  const firstSegmentItems = segments[0].split('_');
  firstSegmentItems.forEach((item) => {
    const match = item.match(/#(\d+)/);
    if (match) {
      // All items in the first segment go to Lvl0
      output.Lvl0.push(match[1]);
    }
  });

  // Process subsequent segments
  segments.slice(1).forEach((segment) => {
    const items = segment.split('_');
    items.forEach((item) => {
      const match = item.match(/#(\d+)/);
      const parentMatch = item.match(/\^(\d+)/);
      const id = match ? match[1] : null;
      const parentId = parentMatch ? parentMatch[1] : null;

      if (parentId) {
        // Parent ID context, should be in Lvl0
        if (!output.Lvl0.includes(parentId)) {
          output.Lvl0.push(parentId);
        }
      }

      if (id) {
        // ID context, should be in Lvl1 if it has a parentId
        if (parentId) {
          output.Lvl1.push(id);
        } else {
          // Otherwise, it should be in Lvl0
          if (!output.Lvl0.includes(id)) {
            output.Lvl0.push(id);
          }
        }
      }
    });
  });

  return output;
};

/**
 * Parses a hierarchical string into two levels of IDs based on segments separated by '>'.
 * Each segment contains items separated by '_', with IDs prefixed by '#'.
 * - Input: "cat#1>subcat#2_item#3>subsubcat#4"
 * - Output: { "Lvl0": ["1", "3"], "Lvl1": ["2", "4"] }
 */
export const parseHierarchicalCategoryString = (input: string): Record<string, string[]> => {
  const output: Record<string, string[]> = {
    Lvl0: [],
    Lvl1: [],
  };

  // Split the input string into hierarchical segments based on '>'
  const segments = input.split('>');

  // Process the first segment as Lvl0
  const processFirstSegment = (segment: string) => {
    const items = segment.split('_');
    output.Lvl0.push(...(items.map((item) => item.match(/#(\d+)/)?.[1]).filter(Boolean) as string[]));
  };

  // Process the remaining segments
  const processSegment = (segment: string) => {
    const items = segment.split('_');
    if (items.length > 0) {
      const lastItem = items.pop(); // The last item is treated as Lvl0
      if (lastItem) {
        const lastLvl0Id = lastItem.match(/#(\d+)/)?.[1];
        if (lastLvl0Id) {
          output.Lvl0.push(lastLvl0Id);
        }
      }
    }
    output.Lvl1.push(...(items.map((item) => item.match(/#(\d+)/)?.[1]).filter(Boolean) as string[]));
  };

  // Process the first segment
  processFirstSegment(segments[0]);

  // Process all middle segments
  for (let i = 1; i < segments.length - 1; i++) {
    processSegment(segments[i]);
  }

  // The last segment is always considered as Lvl1
  const lastSegmentItems = segments[segments.length - 1].split('_');
  output.Lvl1.push(...(lastSegmentItems.map((item) => item.match(/#(\d+)/)?.[1]).filter(Boolean) as string[]));

  return output;
};

/**
 * Converts a record of applied hot filters into a request payload format.
 * Input: A record where keys are filter names and values are hierarchical or non-hierarchical strings with IDs prefixed by '#'.
 *   {
 *     "category": "cat#1>subcat#2_item#3>subsubcat#4",
 *     "material": "material#4", // Even though material isn't hierarchical, it's still level 0.
 *     "brands": "brand#6_brand#7"
 *   }
 * Output: A record with keys formatted as `<key>Lvl0.id` or `<key>.id` and arrays of extracted IDs.
 *   {
 *     "categoryLvl0.id": ["1", "3"],
 *     "categoryLvl1.id": ["2", "4"],
 *     "materialLvl0.id": ["4"],
 *     "brands.id": ["6", "7"]
 *   }
 */
export const convertAppliedHotFiltersToPayload = (input: Record<string, string>): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(input)) {
    if (value?.includes('>')) {
      // Can be both material and category
      // Handle hierarchical values
      const parsed = key === 'category' ? parseCategoryUrlFragment(value.split('__')) : parseHierarchicalString(value);
      result[`${key}Lvl0.id`] = parsed.Lvl0;
      result[`${key}Lvl1.id`] = parsed.Lvl1;
    } else {
      // Handle non-hierarchical values
      if (/#[0-9]+/.test(value)) {
        const ids = value
          .split('_')
          .map((item) => item.match(/#(\d+)$/)?.[1])
          .filter((id) => id !== null) as string[];

        // Special case for 'material' key
        if (key === 'material') {
          if (ids.length > 0) {
            result[`${key}Lvl0.id`] = ids;
          }
        } else {
          if (ids.length > 0) {
            result[`${key}.id`] = ids;
          }
        }
      }
    }
  }

  // Filter the result keys to match the allowed fields
  const filteredResult: Record<string, string[]> = {};

  for (const key of Object.keys(result)) {
    if (ALLOWED_HOT_FILTER_FIELDS.some((field) => key.startsWith(field))) {
      filteredResult[key] = result[key];
    }
  }

  return filteredResult;
};

/**
 * Creates unique keys by combining the base key, filter ID, and level to avoid conflicts.
 * For example, if both "Clothing" and "Men" have ID `2`, they are distinguished as `category_2_1` and `universe_2_1`.
 * This method ensures correct hierarchy even when IDs overlap between different filter categories.
 */
export const processFilterItems = (
  dataKey: string,
  items: HotFilterItem[],
  map: Record<string, HotFilterItem>,
  result: HotFilterItem[],
): void => {
  const baseKey = dataKey.replace(/Lvl\d+$/, '');
  const currentLevel = Number(dataKey.match(/\d+/)?.[0] ?? -1);

  // Since universe IDs and filterIds can be the same, and parent IDs might overlap across levels, we use the level in the key to avoid incorrect mapping and ensure proper hierarchy.
  items.forEach((item) => {
    const itemKey = `${baseKey}_${item.filterId}_${currentLevel}`;
    const mappedItem = { ...item, id: item.filterId, children: [], parentKey: dataKey };

    map[itemKey] = mappedItem;

    const parentKey = item.parentID ? `${baseKey}_${item.parentID}_${currentLevel - 1}` : baseKey;

    if (item.parentID && map[parentKey]) {
      map[parentKey].children.push(mappedItem);
    } else {
      if (!map[baseKey]) {
        map[baseKey] = {
          id: baseKey,
          filterId: baseKey,
          name: baseKey,
          children: [],
          isNested: dataKey.includes('Lvl'),
        };
        result.push(map[baseKey]);
      }
      map[baseKey].children.push(mappedItem);
    }
  });
};
